import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNotification } from '../common';

const UserProfileModal = ({ isOpen, onClose, userId }) => {
  const { showNotification } = useNotification();
  
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    phone: '',
    email_signature: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserData();
    }
  }, [isOpen, userId]);
  
  // Add escape key listener to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (isOpen && e.key === 'Escape') {
        onClose();
      }
    };
    
    // Add event listener when component mounts or when isOpen changes
    document.addEventListener('keydown', handleEscapeKey);
    
    // Clean up event listener when component unmounts or when isOpen changes
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get('/api/auth/me', config);
      
      setUserData({
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        email_signature: res.data.email_signature || ''
      });
    } catch (err) {
      console.error('Error fetching user data:', err);
      showNotification('Failed to load user data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Just update the email signature
      await axios.patch(
        '/api/auth/users/' + userId + '/signature', 
        { email_signature: userData.email_signature },
        config
      );
      
      showNotification('Your profile has been updated successfully', 'success');
      // Close the modal after successful update
      onClose();
    } catch (err) {
      console.error('Error updating profile:', err);
      showNotification('Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Create refs for the first and last focusable elements
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);
  
  // Handle tab key for focus trapping
  const handleTabKey = (e) => {
    if (!isOpen) return;
    
    // Check if Shift+Tab
    if (e.key === 'Tab' && e.shiftKey) {
      if (document.activeElement === firstFocusableRef.current) {
        e.preventDefault();
        lastFocusableRef.current.focus();
      }
    } 
    // Check if Tab
    else if (e.key === 'Tab') {
      if (document.activeElement === lastFocusableRef.current) {
        e.preventDefault();
        firstFocusableRef.current.focus();
      }
    }
  };
  
  // Focus management
  useEffect(() => {
    if (isOpen && firstFocusableRef.current) {
      // Set a timeout to allow the component to render fully
      setTimeout(() => {
        firstFocusableRef.current.focus();
      }, 50);
    }
  }, [isOpen, loading]);
  
  // Add tab key handling
  useEffect(() => {
    document.addEventListener('keydown', handleTabKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen]);
  
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto" 
      aria-labelledby="modal-title" 
      role="dialog" 
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true" 
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div 
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          tabIndex="-1"
        >
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Your Profile
                </h3>
                
                {loading ? (
                  <div className="mt-4 flex justify-center">
                    <div className="loader"></div>
                  </div>
                ) : (
                  <div className="mt-4">
                    {/* Notifications are now handled by the notification system */}
                    
                    <form onSubmit={handleSubmit}>
                      {/* User info fields - read only */}
                      <div className="mb-4 bg-gray-50 p-4 rounded-md">
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name
                          </label>
                          <input
                            type="text"
                            value={userData.name}
                            disabled
                            className="shadow appearance-none border rounded w-full py-1.5 px-2 bg-gray-100 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          />
                        </div>
                        
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={userData.email}
                            disabled
                            className="shadow appearance-none border rounded w-full py-1.5 px-2 bg-gray-100 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone
                          </label>
                          <input
                            type="text"
                            value={userData.phone}
                            disabled
                            className="shadow appearance-none border rounded w-full py-1.5 px-2 bg-gray-100 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          />
                        </div>
                      </div>
                      
                      {/* Email signature - editable */}
                      <div className="mb-4">
                        <label htmlFor="email_signature" className="block text-sm font-medium text-gray-700 mb-1">
                          Email Signature
                        </label>
                        <div className="border border-gray-300 rounded-md p-2 bg-gray-50 mb-2">
                          <p className="text-xs text-gray-500 mb-2">
                            You can use HTML for formatting. Examples:
                          </p>
                          <ul className="text-xs text-gray-500 mb-2 list-disc pl-5">
                            <li>&lt;b&gt;Bold text&lt;/b&gt; for <b>Bold text</b></li>
                            <li>&lt;i&gt;Italic text&lt;/i&gt; for <i>Italic text</i></li>
                            <li>&lt;br&gt; for line breaks</li>
                            <li>&lt;a href="https://example.com"&gt;Link text&lt;/a&gt; for <a href="#" className="text-blue-600">Link text</a></li>
                          </ul>
                          <textarea
                            id="email_signature"
                            name="email_signature"
                            value={userData.email_signature || ''}
                            onChange={handleChange}
                            rows="6"
                            className="w-full p-2 border border-gray-300 rounded-md font-mono text-sm"
                            placeholder="Enter your email signature here..."
                            style={{ whiteSpace: 'pre-wrap' }}
                          />
                        </div>
                        
                        {userData.email_signature && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Signature Preview
                            </label>
                            <div className="border border-gray-300 rounded-md p-3 bg-white">
                              <div className="p-3 border-t border-gray-200 mt-2">
                                <div 
                                  className="prose max-w-none"
                                  dangerouslySetInnerHTML={{ __html: userData.email_signature }}
                                  style={{ 
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                    fontSize: '14px',
                                    lineHeight: '1.6',
                                    whiteSpace: 'pre-wrap'
                                  }}
                                />
                              </div>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              This is how your signature will appear at the bottom of emails you send.
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={onClose}
                          ref={firstFocusableRef}
                          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={saving}
                          ref={lastFocusableRef}
                          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;